import { useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Maximize2, Undo2, Redo2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const HAS_HTML = /<[a-z][\s\S]*>/i;
export const toEditorHtml = (v) => {
  if (!v) return '';
  if (HAS_HTML.test(v)) return v;
  return `<p>${v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')}</p>`;
};

const TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['link'],
  ['clean'],
];

function QuillArea({ value, onChange, placeholder, minHeight, quillRef }) {
  const modules = useMemo(() => ({ toolbar: TOOLBAR, history: { delay: 400, userOnly: true } }), []);
  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={toEditorHtml(value)}
      onChange={(v) => onChange(v === '<p><br></p>' ? '' : v)}
      placeholder={placeholder}
      modules={modules}
      style={{ minHeight }}
      className="rich-notes-quill bg-white"
    />
  );
}

export function RichTextEditor({ value, onChange, placeholder = 'Write notes…', minHeight = 140, testId = 'rich-notes-editor', label = '' }) {
  const [full, setFull] = useState(false);
  const inlineRef = useRef(null);
  const fullRef = useRef(null);

  const hist = (ref, dir) => {
    const ed = ref.current?.getEditor();
    if (ed) ed.history[dir]();
  };

  const controls = (ref, expandBtn = true) => (
    <div className="flex items-center gap-1 justify-end mb-1">
      <button type="button" onClick={() => hist(ref, 'undo')} title="Undo" className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`${testId}-undo`}>
        <Undo2 className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => hist(ref, 'redo')} title="Redo" className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`${testId}-redo`}>
        <Redo2 className="w-3.5 h-3.5" />
      </button>
      {expandBtn && (
        <button type="button" onClick={() => setFull(true)} title="Expand to full screen" className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`${testId}-expand`}>
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <div data-testid={testId}>
      {controls(inlineRef)}
      <QuillArea value={value} onChange={onChange} placeholder={placeholder} minHeight={minHeight} quillRef={inlineRef} />
      {full && (
        <Dialog open onOpenChange={(v) => !v && setFull(false)}>
          <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col" data-testid={`${testId}-fullscreen`}>
            <DialogHeader>
              <DialogTitle>{label || 'Edit Notes'}</DialogTitle>
            </DialogHeader>
            <div className="flex justify-end gap-1">
              {controls(fullRef, false)}
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <QuillArea value={value} onChange={onChange} placeholder={placeholder} minHeight={'55vh'} quillRef={fullRef} />
            </div>
            <div className="flex justify-end pt-2">
              <Button size="sm" className="bg-magenta hover:bg-magenta/90" onClick={() => setFull(false)} data-testid={`${testId}-fullscreen-done`}>Done</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export function HtmlView({ html, className = '', testId = 'html-view' }) {
  if (!html) return <span className="text-slate-400">—</span>;
  if (!HAS_HTML.test(html)) {
    return <div className={`whitespace-pre-wrap break-words ${className}`} data-testid={testId}>{html}</div>;
  }
  return (
    <div
      className={`rich-html-view break-words ${className}`}
      data-testid={testId}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
